import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

// Material UI components
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';

// Material UI icons
import {
  Add as AddIcon,
  Warehouse as WarehouseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Print as PrintIcon} from '@mui/icons-material';

// Import AlertTitle

// Services
import { getLocations, deleteLocation } from '../../services/locationService.js';
import { getWarehouses } from '../../services/warehouseService.js';

// Components
import BarcodePrintDialog from '../../components/BarcodePrintDialog.js';

const LocationList = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // State
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [locationCounts, setLocationCounts] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  // Fetch locations and warehouses on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch warehouses first
        const warehousesData = await getWarehouses();
        setWarehouses(warehousesData);

        // Fetch locations with filter if set, including inventory counts
        const params = warehouseFilter ? { warehouse_id: warehouseFilter, include_counts: true } : { include_counts: true };
        const locationsData = await getLocations(params);
        setLocations(locationsData);

        // Extract inventory counts from the optimized response
        const counts = {};
        locationsData.forEach(location => {
          counts[location.id] = parseInt(location.inventory_count) || 0;
        });
        setLocationCounts(counts);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load locations. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [warehouseFilter]);

  // Group locations by warehouse and then by rack
  const groupedLocations = locations.reduce((acc, location) => {
    const warehouseName = location.warehouse?.name || 'Unknown Warehouse';
    if (!acc[warehouseName]) {
      acc[warehouseName] = {};
    }
    if (!acc[warehouseName][location.rack]) {
      acc[warehouseName][location.rack] = [];
    }
    acc[warehouseName][location.rack].push(location);
    return acc;
  }, {});

  // Sort shelves within each rack
  Object.keys(groupedLocations).forEach(warehouse => {
    Object.keys(groupedLocations[warehouse]).forEach(rack => {
      groupedLocations[warehouse][rack].sort((a, b) => {
        // Try to sort numerically if possible, otherwise alphabetically
        const aShelf = isNaN(a.shelf) ? a.shelf : parseInt(a.shelf);
        const bShelf = isNaN(b.shelf) ? b.shelf : parseInt(b.shelf);

        if (typeof aShelf === 'number' && typeof bShelf === 'number') {
          return aShelf - bShelf;
        }
        return a.shelf.localeCompare(b.shelf);
      });
    });
  });

  // Helper function to determine capacity based on item count
  const getCapacityInfo = (locationId) => {
    const count = locationCounts[locationId] || 0;

    let capacity, color;
    if (count > 30) {
      capacity = 'High';
      color = 'success';
    } else if (count > 15) {
      capacity = 'Medium';
      color = 'warning';
    } else {
      capacity = 'Low';
      color = 'error';
    }

    return { capacity, color };
  };

  // Helper function to get capacity chip
  const getCapacityChip = (locationId) => {
    const { capacity, color } = getCapacityInfo(locationId);

    return (
      <Chip
        label={capacity}
        color={color}
        size="small"
        variant={color === 'default' ? 'outlined' : 'filled'}
      />
    );
  };

  // Handle delete location
  const handleDeleteClick = (location) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteLocation(locationToDelete.id);
      setLocations(locations.filter(loc => loc.id !== locationToDelete.id));
      setSnackbar({
        open: true,
        message: 'Location deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting location:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete location',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      setLoading(true);
      const params = warehouseFilter ? { warehouse_id: warehouseFilter, include_counts: true } : { include_counts: true };
      const locationsData = await getLocations(params);
      setLocations(locationsData);

      // Extract inventory counts from the optimized response
      const counts = {};
      locationsData.forEach(location => {
        counts[location.id] = parseInt(location.inventory_count) || 0;
      });
      setLocationCounts(counts);

      setLoading(false);
      setSnackbar({
        open: true,
        message: 'Locations refreshed',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error refreshing locations:', err);
      setError('Failed to refresh locations. Please try again.');
      setLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    setWarehouseFilter(event.target.value);
  };

  // Handle view items
  const handleViewItems = (locationId) => {
    navigate(`/locations/${locationId}/inventory`);
  };

  // Handle edit location
  const handleEditLocation = (locationId) => {
    navigate(`/locations/edit/${locationId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with title and actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Location Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterListIcon />}
            onClick={() => setFilterOpen(!filterOpen)}
          >
            Filter
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={() => setPrintDialogOpen(true)}
            disabled={loading}
          >
            Print Barcodes
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            component={RouterLink}
            to="/warehouses"
            startIcon={<WarehouseIcon />}
          >
            Manage Warehouses
          </Button>
          <Button
            variant="contained"
            color="secondary"
            component={RouterLink}
            to="/warehouses/add"
            startIcon={<AddIcon />}
          >
            Add Warehouse
          </Button>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/locations/add"
            startIcon={<AddIcon />}
          >
            Add Location
          </Button>
        </Box>
      </Box>

      {/* Filter section */}
      {filterOpen && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Filter Locations</Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="warehouse-filter-label">Warehouse</InputLabel>
            <Select
              labelId="warehouse-filter-label"
              id="warehouse-filter"
              value={warehouseFilter}
              label="Warehouse"
              onChange={handleFilterChange}
            >
              <MenuItem value="">All Warehouses</MenuItem>
              {warehouses.map(warehouse => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {/* Loading and error states */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* No locations message */}
      {!loading && locations.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No locations found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {warehouseFilter ? 'Try selecting a different warehouse or clear the filter.' : 'Add your first location to get started.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/locations/add"
            startIcon={<AddIcon />}
          >
            Add New Location
          </Button>
        </Paper>
      )}

      {/* Location list grouped by warehouse and rack */}
      {!loading && locations.length > 0 && Object.entries(groupedLocations).map(([warehouse, racks]) => {
        const totalLocations = Object.values(racks).reduce((sum, rackLocations) => sum + rackLocations.length, 0);

        return (
          <Paper
            key={warehouse}
            elevation={2}
            sx={{
              mb: 3,
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{
              p: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarehouseIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  {warehouse}
                </Typography>
              </Box>
              <Chip
                label={`${totalLocations} location${totalLocations !== 1 ? 's' : ''}`}
                color="default"
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </Box>

            {/* Render each rack as a separate row */}
            {Object.entries(racks)
              .sort(([rackA], [rackB]) => {
                // Sort racks numerically if possible, otherwise alphabetically
                const aRack = isNaN(rackA) ? rackA : parseInt(rackA);
                const bRack = isNaN(rackB) ? rackB : parseInt(rackB);

                if (typeof aRack === 'number' && typeof bRack === 'number') {
                  return aRack - bRack;
                }
                return rackA.localeCompare(rackB);
              })
              .map(([rack, rackLocations]) => (
                <Box key={`${warehouse}-${rack}`} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                  {/* Rack header */}
                  <Box sx={{
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    <Typography variant="subtitle2" fontWeight="medium" color="text.secondary">
                      Rack {rack}
                    </Typography>
                  </Box>

                  {/* Shelves in this rack */}
                  <Grid container spacing={0}>
                    {rackLocations.map((location, index) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        xl={2.4}
                        key={location.id}
                        sx={{
                          borderRight: {
                            xs: 'none',
                            sm: index % 2 === 0 ? `1px solid ${theme.palette.divider}` : 'none',
                            md: index % 3 !== 2 ? `1px solid ${theme.palette.divider}` : 'none',
                            lg: index % 4 !== 3 ? `1px solid ${theme.palette.divider}` : 'none',
                            xl: index % 5 !== 4 ? `1px solid ${theme.palette.divider}` : 'none'
                          },
                          borderBottom: index < rackLocations.length - Math.ceil(rackLocations.length / 5) ? `1px solid ${theme.palette.divider}` : 'none',
                        }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              Rack {location.rack} - Shelf {location.shelf}
                            </Typography>
                            {getCapacityChip(location.id)}
                          </Box>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {locationCounts[location.id] || 0} items stored at this location
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewItems(location.id)}
                              startIcon={<ArrowForwardIcon />}
                              sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                            >
                              View Items
                            </Button>
                            <Tooltip title="Edit Location">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditLocation(location.id)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Location">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(location)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
          </Paper>
        );
      })}



      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Location</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the location Rack {locationToDelete?.rack} - Shelf {locationToDelete?.shelf}?
            {locationCounts[locationToDelete?.id] > 0 && (
              <Typography color="error" sx={{ mt: 2 }}>
                Warning: This location has {locationCounts[locationToDelete?.id]} items. These items must be moved before deletion.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Barcode Print Dialog */}
      <BarcodePrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
      />
    </Container>
  );
};

export default LocationList;
